import useToolbarDropdowns from './useToolbarDropdowns';
import useToolbarLayout from './useToolbarLayout';
import useToolbarSearch from './useToolbarSearch';

export interface UseToolbarStateOptions {
  searchValue?: string;
  visibleColumns?: Record<string, boolean>;
  sortOptions?: Array<{ field: string; label: string }>;
  groupOptions?: Array<{ field: string; label: string }>;
  onSearchChange?: (value: string) => void;
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onGroupChange?: (field: string | null) => void;
  onFilterClick?: (eventOrFieldId?: React.MouseEvent<HTMLElement> | string) => void;
  leftButtonLabels?: string[];
  rightButtonsCount?: number;
  showSearch?: boolean;
  showColumns?: boolean;
  showSort?: boolean;
  showGroup?: boolean;
  showFilter?: boolean;
  showViewToggle?: boolean;
  showVariantToggle?: boolean;
}

export const useToolbarState = ({
  searchValue = '',
  visibleColumns = {},
  sortOptions = [],
  groupOptions = [],
  onSearchChange,
  onColumnVisibilityChange,
  onGroupChange,
  onFilterClick,
  leftButtonLabels = [],
  rightButtonsCount = 0,
  showSearch = false,
  showColumns = true,
  showSort = true,
  showGroup = true,
  showFilter = true,
  showViewToggle = true,
  showVariantToggle = false,
}: UseToolbarStateOptions) => {
  const search = useToolbarSearch({ searchValue, onSearchChange });

  const layout = useToolbarLayout({
    leftButtonLabels,
    rightButtonsCount,
    searchOpen: search.searchOpen,
    showSearch,
    showColumns,
    showSort,
    showGroup,
    showFilter,
    showViewToggle,
    showVariantToggle,
    sortOptionsCount: sortOptions.length,
    groupOptionsCount: groupOptions.length,
  });

  const dropdowns = useToolbarDropdowns({
    visibleColumns,
    groupOptions,
    onColumnVisibilityChange,
    onGroupChange,
    onFilterClick,
  });

  return {
    collapseLeft: layout.collapseLeft,
    collapseMiddle: layout.collapseMiddle,
    searchOpen: search.searchOpen,
    localSearchValue: search.localSearchValue,
    columnsAnchor: dropdowns.columnsAnchor,
    filterAnchor: dropdowns.filterAnchor,
    toolbarRef: layout.toolbarRef,
    searchInputRef: search.searchInputRef,
    handleColumnsClick: dropdowns.handleColumnsClick,
    handleColumnsClose: dropdowns.handleColumnsClose,
    handleColumnToggle: dropdowns.handleColumnToggle,
    handleFilterClick: dropdowns.handleFilterClick,
    handleFilterClose: dropdowns.handleFilterClose,
    handleGroupSelect: dropdowns.handleGroupSelect,
    handleSearchClick: search.handleSearchClick,
    handleSearchChange: search.handleSearchChange,
    handleSearchBlur: search.handleSearchBlur,
    handleSearchKeyDown: search.handleSearchKeyDown,
    groupDropdownOptions: dropdowns.groupDropdownOptions,
  };
};

export default useToolbarState;
